import { useCallback, useEffect, useRef, useState } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { ConfirmDialog, useIsMobileOrTabletDevice } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../constants/i18n"
import { useMapDispatchContext } from "../../contexts/MapContext"

import { GpsLocationButton } from "../GpsLocationButton"
import { RecenterButton } from "../RecenterButton"

type LocationButtonProps = {
    onRecenter: () => void
    onRecenterOnUserLocation: (_crd: GeolocationCoordinates | null) => void
}

export const LocationButton = (props: LocationButtonProps) => {
    const {
        onRecenter,
        onRecenterOnUserLocation
    } = props

    const watchIdRef = useRef<number | null>(null)
    const [geoLocationRunning, setGeoLocationRunning] = useState(false)
    const geoLocationRunningRef = useRef(false)
    const [permissionNotGranted, setPermissionNotGranted] = useState(false)
    const { t } = useTranslation(I18N_NAMESPACE)
    const isMobileOrTablet = useIsMobileOrTabletDevice()
    const { setUserLocation } = useMapDispatchContext()
    const setUserLocationRef = useRef(setUserLocation)
    const onRecenterOnUserLocationRef = useRef(onRecenterOnUserLocation)
    const [showDialog, setShowDialog] = useState(false)

    useEffect(() => {
        geoLocationRunningRef.current = geoLocationRunning
        setUserLocationRef.current = setUserLocation
        onRecenterOnUserLocationRef.current = onRecenterOnUserLocation
    }, [geoLocationRunning, setUserLocation, onRecenterOnUserLocation])

    useEffect(() => {
        let permissionStatus: PermissionStatus | null = null

        const handlePermissionChange = () => {
            setPermissionNotGranted(permissionStatus?.state === "denied")
        }

        navigator.permissions.query({ name: "geolocation" })
            .then((result) => {
                permissionStatus = result
                handlePermissionChange()
                permissionStatus.addEventListener("change", handlePermissionChange)
            });

        return () => {
            permissionStatus?.removeEventListener("change", handlePermissionChange)
        }
    }, [])

    const handleGeoLocationSuccess = useCallback((pos: GeolocationPosition) => {
        const crd = pos.coords

        setUserLocationRef.current(crd)

        if (!geoLocationRunningRef.current) {
            geoLocationRunningRef.current = true
            setGeoLocationRunning(true)
            setPermissionNotGranted(false)
            onRecenterOnUserLocationRef.current(crd)
        }
    }, [])

    const startWatchingPosition = useCallback(() => {
        if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current)
            watchIdRef.current = null
        }

        const onError = (err: GeolocationPositionError) => {
            console.warn("ERROR(" + err.code + "): " + err.message)
            if (err.code === 1) {
                setPermissionNotGranted(true)
                geoLocationRunningRef.current = false
                setGeoLocationRunning(false)
            }
        }

        const options: PositionOptions = {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
        }

        const id = navigator.geolocation.watchPosition(handleGeoLocationSuccess, onError, options)
        watchIdRef.current = id
    }, [handleGeoLocationSuccess])

    useEffect(() => {
        return () => {
            if (watchIdRef.current !== null) {
                navigator.geolocation.clearWatch(watchIdRef.current)
                watchIdRef.current = null
            }
        }
    }, [])

    const handleRecenterOnUserLocation = useCallback(() => {
        if (permissionNotGranted) {
            setShowDialog(true)
        }

        if (watchIdRef.current === null) {
            startWatchingPosition()
        } else {
            onRecenterOnUserLocation(null)
        }
    }, [permissionNotGranted, startWatchingPosition, onRecenterOnUserLocation])

    const handleRecenterButtonClick = useCallback(() => {
        if (!isMobileOrTablet) {
            onRecenter()
            return
        }

        handleRecenterOnUserLocation()
    }, [isMobileOrTablet, onRecenter, handleRecenterOnUserLocation])

    return <>
        {showDialog && (
            <ConfirmDialog
                closeWithCallbacks={() => setShowDialog(false)}
                confirmText={t(I18N_NAMESPACE, "containers.locationButton.permissionNeeded.confirm")}
                onlyConfirm={true}
                open={showDialog}
                text={t(I18N_NAMESPACE, "containers.locationButton.permissionNeeded.message")}
            />
        )}
        {(isMobileOrTablet) ? (
            <GpsLocationButton onClick={handleRecenterOnUserLocation} />
        ) : <RecenterButton onRecenter={handleRecenterButtonClick} />}
    </>
}